import { Alert, Button, Dialog, Icon, Tooltip } from '@sr/ui-library';
import { useState } from 'react';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import {
  CSV_MAX_ROWS,
  CSV_WARN_ROWS,
  type CsvContextResolver,
  type CsvExportSource,
  startCsvExport,
  trackCsvExport,
  useIsCsvExportRunning,
} from '../utils/csv/csvExport';
import { type CsvColumn, csvFileName } from '../utils/csv/toCsvChunk';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { CsvColumnPicker, defaultCsvColumnIds } from './CsvColumnPicker';

/**
 * Called once per export, so lookups cached by the resolver (client names,
 * say) never carry over to the next export. Required whenever the columns read
 * a context, as `resolveContext` is on `CsvExportSource`.
 */
type CreateResolveContextField<T, C> = [C] extends [void]
  ? { createResolveContext?: () => CsvContextResolver<T, C> }
  : { createResolveContext: () => CsvContextResolver<T, C> };

type DownloadCsvButtonProps<T extends { _id: string }, C> = Pick<
  CsvExportSource<T, C>,
  'fetchPage' | 'isRowCountCapped' | 'label' | 'rowCount'
> & {
  /** Every column the page's spec offers. */
  columns: CsvColumn<T, C>[];
  /** Filename and storage part: "titles". */
  fileLabel: string;
  isDisabled?: boolean;
  /** "section" for a tab of a page (Tasks); the dialog copy names it. */
  scope?: 'page' | 'section';
} & CreateResolveContextField<T, C>;

/**
 * The CSV button for a list page header, with its confirm dialog (APP-2436).
 * Downloads every row matching the page's current filters.
 */
export const DownloadCsvButton = <T extends { _id: string }, C>(
  props: DownloadCsvButtonProps<T, C>,
) => {
  const {
    columns,
    fetchPage,
    fileLabel,
    isDisabled,
    isRowCountCapped,
    label,
    rowCount,
    scope = 'page',
  } = props;
  // `CreateResolveContextField` only lets this be missing when `C` is void.
  const { createResolveContext } = props as {
    createResolveContext?: () => CsvContextResolver<T, C>;
  };
  const [isEnabled] = useFeatureFlag(FEATURE_FLAGS.MSR3_DOWNLOAD_CSV);
  const [activeWorkspace] = useActiveWorkspace();
  const isRunning = useIsCsvExportRunning();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set<string>());

  if (!isEnabled) return null;

  const isAgent = workspaceTypeIsAgent(activeWorkspace);
  const offeredColumns = columns.filter(
    (column) => isAgent || !column.agentOnly,
  );
  // Spec order, never the order the boxes were ticked.
  const selectedColumns = offeredColumns.filter((column) =>
    selectedIds.has(column.id),
  );
  const isBlocked = rowCount > CSV_MAX_ROWS;

  const handleOpen = () => {
    if (isBlocked) {
      trackCsvExport({
        column_count: 0,
        page: fileLabel,
        row_count: rowCount,
        status: 'blocked',
      });
    }

    // Always the on-screen columns, never the last download's selection.
    setSelectedIds(defaultCsvColumnIds(offeredColumns));
    setIsOpen(true);
  };

  const handleDownload = () => {
    setIsOpen(false);
    void startCsvExport({
      columns: selectedColumns,
      fetchPage,
      fileLabel,
      fileName: csvFileName(activeWorkspace?.workspaceName, fileLabel),
      isRowCountCapped,
      label,
      resolveContext: createResolveContext?.(),
      rowCount,
    } as CsvExportSource<T, C>);
  };

  return (
    <>
      <Tooltip content={`Download ${label} CSV`}>
        <Button
          disabled={isDisabled || isRunning}
          sectionLeft={<Icon icon="download" />}
          variant="outline"
          onClick={handleOpen}
        >
          CSV
        </Button>
      </Tooltip>

      <Dialog
        heading={`Download ${label} for offline viewing`}
        actionButtonProps={{
          children: 'Download',
          disabled: selectedColumns.length === 0,
          onClick: handleDownload,
        }}
        hasActionButton={!isBlocked}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <div className="space-y-4 text-sm">
          <p>
            This will export all available data on the {label} {scope}. The
            downloaded data will be limited by any active filters. This is a
            static file. Future updates in MyScreenrights will not sync to this
            CSV.
          </p>

          <p className="font-medium">
            This file contains personal information. Store it securely.
          </p>

          {isBlocked ? (
            <Alert variant="warning">
              This view has {rowCount.toLocaleString()} rows. Narrow your
              filters to under {CSV_MAX_ROWS.toLocaleString()} to download.
            </Alert>
          ) : (
            <>
              <CsvColumnPicker
                columns={offeredColumns}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />

              <p>
                {isRowCountCapped
                  ? `More than ${rowCount.toLocaleString()} rows.`
                  : `${rowCount.toLocaleString()} ${rowCount === 1 ? 'row' : 'rows'}.`}
                {/* A floor says nothing about how many rows follow it. */}
                {(isRowCountCapped || rowCount > CSV_WARN_ROWS) &&
                  ' This may take several minutes. Keep this tab open until it finishes.'}
                {/*
                 * The block above can't fire on a floor, so the cap is only
                 * found mid-export; say so before the wait, not after it.
                 */}
                {isRowCountCapped &&
                  ` Downloads stop at ${CSV_MAX_ROWS.toLocaleString()} rows, so narrow your filters first if this view could have more.`}
              </p>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};
